<template>
  <section class="dealer-contacts-form">
    <!-- Background Image Container -->
    <div class="dealer-contacts-form__background">
      <img
        v-if="backgroundImage"
        :src="backgroundImage"
        alt=""
        loading="lazy"
        class="dealer-contacts-form__bg-img" />

      <picture v-else>
        <source
          type="image/avif"
          :srcset="BackgroundImageAvif" />
        <source
          type="image/webp"
          :srcset="BackgroundImageWebp" />
        <img
          :src="BackgroundImage"
          alt=""
          loading="lazy"
          class="dealer-contacts-form__bg-img" />
      </picture>

      <!-- Content Container -->
      <div class="dealer-contacts-form__container">
        <!-- Left Column - White Section -->
        <div class="dealer-contacts-form__left">
          <!-- Contact Information -->
          <div class="dealer-contacts-form__contact-info">
            <h2 class="dealer-contacts-form__contact-title">
              {{ $t('dealer.contactForm.contactTitle2') }}
            </h2>
          </div>

          <!-- CTA Buttons -->
          <div class="dealer-contacts-form__buttons">
            <cta-icon
              v-if="showCallButton && phoneNumber"
              :show-icon="true"
              :text="$t('dealer.contactForm.callButton')"
              :href="phoneHref"
              :anchor="true"
              @click="handleCallClick">
              <template #icon>
                <red-phone-icon />
              </template>

              <template #icon-hover>
                <white-phone-icon />
              </template>
            </cta-icon>

            <cta-icon
              v-if="showWhatsappButton"
              :show-icon="true"
              :text="$t('dealer.contactForm.whatsappButton')"
              :href="whatsappHref"
              @click="handleWhatsappClick">
              <template #icon>
                <red-whatsapp-icon />
              </template>

              <template #icon-hover>
                <white-whatsapp-icon />
              </template>
            </cta-icon>

            <cta-icon
              v-if="showLocationButton"
              :show-icon="true"
              :text="$t('dealer.contactForm.locationButton')"
              @click="handleLocationClick">
              <template #icon>
                <red-pin-icon />
              </template>

              <template #icon-hover>
                <white-pin-icon />
              </template>
            </cta-icon>
          </div>
        </div>

        <!-- Right Column - Red Section with Form -->
        <div class="dealer-contacts-form__right">
          <div class="dealer-contacts-form__form-content">
            <!-- Form Title and Description (hidden after submission) -->
            <div
              v-if="!formSubmitted"
              class="dealer-contacts-form__form-header">
              <h3
                v-if="formTitle"
                class="dealer-contacts-form__form-title"
                v-html="formTitle" />
              <p class="dealer-contacts-form__form-description">
                {{ $t('dealer.contactForm.formDescription') }}
              </p>
            </div>

            <!-- Form Fields
            <div class="dealer-contacts-form__form">
              <div class="dealer-contacts-form__form-grid">
                <div class="dealer-contacts-form__form-row">
                  <div class="dealer-contacts-form__form-field">
                    <vc-input
                      v-model="formData.name"
                      name="name"
                      type="text"
                      :placeholder="$t('dealer.contactForm.placeholders.name')"
                      :required="true" />
                  </div>
                  <div class="dealer-contacts-form__form-field">
                    <vc-input
                      v-model="formData.email"
                      name="email"
                      type="email"
                      :placeholder="$t('dealer.contactForm.placeholders.email')"
                      :required="true" />
                  </div>
                </div>

                <div class="dealer-contacts-form__form-row">
                  <div class="dealer-contacts-form__form-field">
                    <vc-input
                      v-model="formData.city"
                      name="city"
                      type="text"
                      :placeholder="$t('dealer.contactForm.placeholders.city')"
                      :required="true" />
                  </div>
                  <div class="dealer-contacts-form__form-field">
                    <vc-input
                      v-model="formData.phone"
                      name="phone"
                      type="tel"
                      :placeholder="$t('dealer.contactForm.placeholders.phone')"
                      :required="true" />
                  </div>
                </div>

                <div class="dealer-contacts-form__form-row">
                  <div class="dealer-contacts-form__form-field">
                    <select
                      v-model="formData.day"
                      class="dealer-contacts-form__select"
                      required>
                      <option
                        value=""
                        disabled>
                        {{ $t('dealer.contactForm.placeholders.day') }}
                      </option>
                      <option
                        v-for="day in dayOptions"
                        :key="day.value"
                        :value="day.value">
                        {{ day.label }}
                      </option>
                    </select>
                  </div>
                  <div class="dealer-contacts-form__form-field">
                    <select
                      v-model="formData.time"
                      class="dealer-contacts-form__select"
                      required>
                      <option
                        value=""
                        disabled>
                        {{ $t('dealer.contactForm.placeholders.time') }}
                      </option>
                      <option
                        v-for="time in timeOptions"
                        :key="time.value"
                        :value="time.value">
                        {{ time.label }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>

              <div>
                <div class="dealer-contacts-form__form-footer">
                  <a
                    :href="resolvedPrivacyLink"
                    class="dealer-contacts-form__privacy-link"
                    target="_blank"
                    rel="noopener noreferrer">
                    {{ $t('dealer.contactForm.privacy') }}
                  </a>
                </div>

                <div class="dealer-contacts-form__form-footer-cta">
                  <cta-veneta-icon
                    :text="$t('dealer.contactForm.submit')"
                    color="white"
                    color-hover="red"
                    :color-change="true"
                    @click="handleSubmit" />
                </div>
              </div>
            </div>
            -->
            <!-- Store selector (multi-store) -->
            <div
              v-if="stores.length > 1 && !formSubmitted"
              class="dealer-contacts-form__store-selector">
              <label class="dealer-contacts-form__store-label">
                {{ $t('promo.headquarters.label') }}
              </label>
              <select
                class="dealer-contacts-form__store-select"
                @change="onStoreSelected($event.target.value)">
                <option
                  value=""
                  disabled
                  selected>
                  {{ $t('promo.headquarters.placeholder') }}
                </option>
                <option
                  v-for="opt in storeSelectOptions"
                  :key="opt.value"
                  :value="opt.value">
                  {{ opt.label }}
                </option>
              </select>
            </div>

            <form-builder
              :id="`block-form-reservedArea`"
              class="rivenditore-page__form"
              :wrapped="false"
              :handle="formHandle"
              :params="[
                { key: 'dealerEmail', value: email || 'info@venetacucine.com' },
                { key: 'dealerName', value: store || 'Store senza nome' },
                ...extraFormParams,
              ]"
              :disabled="stores.length > 1 && !selectedStoreId"
              :keep-success-message="keepSuccessMessage"
              :success-title="successTitle"
              :success-message="successMessage"
              :field-options-overrides="fieldOptionsOverrides"
              @submitted="onsubmit" />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
  import {
    computed, nextTick, ref
  } from 'vue'

  import BackgroundImageAvif from '~/assets/images/background.avif'
  import BackgroundImage from '~/assets/images/background.jpg'
  import BackgroundImageWebp from '~/assets/images/background.webp'
  import RedPhoneIcon from '~/assets/svgs/icon/red/phone.svg'
  import RedPinIcon from '~/assets/svgs/icon/red/pin.svg'
  import RedWhatsappIcon from '~/assets/svgs/icon/red/whatsapp-logo.svg'
  import WhitePhoneIcon from '~/assets/svgs/icon/white/phone.svg'
  import WhitePinIcon from '~/assets/svgs/icon/white/pin.svg'
  import WhiteWhatsappIcon from '~/assets/svgs/icon/white/whatsapp-logo.svg'
  import { buildTelUrl, buildWaMeUrl } from '~/utils/phone'
  import { getStoreSearchDisplayTitle } from '~/utils/stores'

  // Props
  const props = defineProps({
    backgroundImage: {
      type: String
    },
    privacyLink: {
      type: String
    },
    email: {
      type: String
    },
    store: {
      type: String
    },
    phoneNumber: {
      type: String
      // default: '06 7978 3499'
    },
    whatsappNumber: {
      type: String,
      default: null
    },
    showCallButton: {
      type: Boolean,
      default: true
    },
    showWhatsappButton: {
      type: Boolean,
      default: true
    },
    showLocationButton: {
      type: Boolean,
      default: true
    },
    formTitle: {
      type: String,
      default: ''
    },
    formHandle: {
      type: String,
      default: 'bookAppointment'
    },
    extraFormParams: {
      type: Array,
      default: () => []
    },
    fieldOptionsOverrides: {
      type: Object,
      default: () => ({})
    },
    stores: {
      type: Array,
      default: () => []
    },
    keepSuccessMessage: {
      type: Boolean,
      default: false
    },
    successTitle: {
      type: String,
      default: ''
    },
    successMessage: {
      type: String,
      default: ''
    },
    dayOptions: {
      type: Array,
      default() {
        const { t } = useI18n()
        return [
          { value: 'lunedi', label: t('dealer.contactForm.dayOptions.monday') },
          { value: 'martedi', label: t('dealer.contactForm.dayOptions.tuesday') },
          { value: 'mercoledi', label: t('dealer.contactForm.dayOptions.wednesday') },
          { value: 'giovedi', label: t('dealer.contactForm.dayOptions.thursday') },
          { value: 'venerdi', label: t('dealer.contactForm.dayOptions.friday') },
          { value: 'sabato', label: t('dealer.contactForm.dayOptions.saturday') }
        ]
      }
    },
    timeOptions: {
      type: Array,
      default: () => [
        { value: '09:00', label: '09:00' },
        { value: '10:00', label: '10:00' },
        { value: '11:00', label: '11:00' },
        { value: '14:00', label: '14:00' },
        { value: '15:00', label: '15:00' },
        { value: '16:00', label: '16:00' },
        { value: '17:00', label: '17:00' }
      ]
    }
  })

  // Emits
  const emit = defineEmits([
    'call-click',
    'whatsapp-click',
    'location-click',
    'form-submit',
    'store-selected'
  ])

  // Reactive state
  const formData = ref({
    name: '',
    email: '',
    city: '',
    phone: '',
    day: '',
    time: ''
  })

  const isSubmitting = ref(false)
  const formSubmitted = ref(false)

  const { t } = useI18n()
  const { bus } = useNuxtApp()

  const resolvedPrivacyLink = computed(() => {
    return props.privacyLink || t('dealer.contactForm.privacyLink')
  })

  const phoneHref = computed(() => buildTelUrl(props.phoneNumber))
  const whatsappHref = computed(() => buildWaMeUrl(props.whatsappNumber))

  // Methods
  const handleCallClick = () => {
    emit('call-click')
  }

  const handleWhatsappClick = () => {
    emit('whatsapp-click')
  }

  const handleLocationClick = () => {
    emit('location-click')
  }

  const handleSubmit = async () => {
    if (isSubmitting.value) return

    // Basic validation
    const requiredFields = [
      'name',
      'email',
      'city',
      'phone',
      'day',
      'time'
    ]
    const isValid = requiredFields.every(field => formData.value[field]?.trim())

    if (!isValid) {
      // You might want to show validation errors here
      return
    }

    isSubmitting.value = true

    try {
      await new Promise(resolve => setTimeout(resolve, 1000)) // Simulate API call
      emit('form-submit', { ...formData.value })

      // Reset form after successful submission
      formData.value = {
        name: '',
        email: '',
        city: '',
        phone: '',
        day: '',
        time: ''
      }
    } catch (error) {
      console.error('Form submission error:', error)
    } finally {
      isSubmitting.value = false
    }
  }

  const onsubmit = params => {
    formSubmitted.value = true
    emit('form-submit', { ...params })
  }

  // Store selector logic (multi-store promo)
  const selectedStoreId = ref(null)

  const storeSelectOptions = computed(() =>
    props.stores.map(s => ({
      value: String(s.id),
      label: getStoreSearchDisplayTitle(s)
    }))
  )

  const onStoreSelected = async id => {
    selectedStoreId.value = id
    const store = props.stores.find(s => String(s.id) === String(id))
    emit('store-selected', store)
    await nextTick()
    bus.emit('scroll:refresh')
  }
</script>

<style lang="scss" scoped>
.dealer-contacts-form {
  width: 100%;
  min-height: 100vh;
  position: relative;

  &__background {
    position: relative;
    width: 100%;
    min-height: 100vh;
    overflow: hidden;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    padding: 4rem 0;
  }

  &__bg-img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    z-index: 1;
    filter: brightness(0.5);
  }

  &__container {
    position: relative;
    z-index: 3;
    display: flex;
    flex-direction: column;
    max-width: 100%;

    @include mq(medium) {
      @include wrapper;

      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      justify-content: center;
      align-items: stretch;
      max-width: 1160px;
    }
  }

  &__left {
    background: var(--color-white);
    padding: var(--space-n) var(--space-s); // 32px 24px from Figma
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-xxs); // 10px from Figma
    min-height: 635px; // From Figma
    // height: 100%;
    width: 100%;
    box-sizing: border-box;

    @include up-to-desktop {
      min-height: auto;
      padding: var(--space-l);
    }

    @include up-to-tablet {
      padding: var(--space-n);
    }
  }

  &__contact-info {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-xxs); // 8px from Figma
    width: 100%;
  }

  &__contact-title {
    font-family: var(--font-family-poppins);
    font-weight: 500;
    font-size: var(--typography-size-l-desktop);
    line-height: 1.4;
    text-align: center;
    color: var(--color-black);
    margin: 0;

    @include up-to-tablet {
      font-size: var(--typography-size-l-mobile);
    }
  }

  &__phone {
    font-family: var(--font-family-poppins);
    font-weight: 500;
    font-size: var(--typography-size-ui-kit-32);
    line-height: 1.4;
    text-align: center;
    color: var(--color-black);
    margin: 0;
  }

  &__buttons {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    width: 100%;
  }

  &__right {
    background: var(--color-red);
    padding: var(--space-n) var(--space-s); // 32px 24px from Figma
    width: 100%;
    box-sizing: border-box;

    .input__error,
    .input-checkbox__error {
      color: var(--color-white);
    }

    :deep(.input.-has-error) {
      --input-error-expanding-border: rgb(255 255 255 / 1);
      --input-error-border-radius: 2px;
      --input-error-expanded-border-radius: 6px;
    }

    :deep(.input.-has-error .input__wr) {
      border-color: var(--color-white);
    }
  }

  &__form-content {
    display: flex;
    flex-direction: column;
    gap: 24px; // Exact spacing from Figma design
    :deep(.input-checkbox) {
      margin-top: 10px;
    }
  }

  &__form-header {
    display: flex;
    flex-direction: column;
    gap: var(--space-xxs); // 8px from Figma
  }

  &__form-title {
    font-family: var(--font-family-poppins);
    font-weight: 600;
    font-size: var(--typography-size-l-desktop);
    line-height: 1.2;
    letter-spacing: 0.02em;
    color: var(--color-white);
    margin: 0;
    padding-top: 0;

    strong {
      font-weight: 700;
    }

    @include up-to-tablet {
      font-size: var(--typography-size-l-mobile);
    }
  }

  &__store-selector {
    margin-bottom: 24px;
  }

  &__store-label {
    display: block;
    font-family: var(--font-family-poppins);
    font-size: var(--text-b-n);
    color: var(--color-white);
    margin-bottom: 8px;
  }

  &__store-select {
    width: 100%;
    padding: var(--space-xxs) var(--space-xs);
    height: 53.25px;
    border: 1px solid var(--color-grey);
    border-radius: 0;
    background: var(--color-white);
    font-family: var(--font-family-poppins);
    font-size: var(--text-b-n);
    color: var(--color-black);
    box-sizing: border-box;
    transition: border-color var(--timing-default) ease;
    appearance: none;
    cursor: pointer;
    padding-right: calc(var(--space-xs) + 24px);

    &:focus {
      outline: none;
      border-color: var(--color-black);
      box-shadow: 0 2px 12px rgb(0, 0, 0, 0.15);
    }

    &:hover {
      border-color: var(--color-black);
      box-shadow: 0 2px 8px rgb(0, 0, 0, 0.1);
    }

    &:invalid {
      color: var(--color-dark-grey);
    }

    @include up-to-tablet {
      height: 44px;
      padding: 6px 12px;
    }
  }

  &__form-description {
    font-family: var(--font-family-poppins);
    font-weight: 500;
    font-size: var(--typography-size-m-desktop);
    line-height: 1.2;
    letter-spacing: 0.02em;
    color: var(--color-white);
    margin: 0;

    @include up-to-tablet {
      font-size: var(--typography-size-m-mobile);
    }
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: var(--space-m); // 40px from Figma
  }

  &__form-grid {
    display: flex;
    flex-direction: column;
    gap: var(--space-m); // 40px from Figma
  }

  &__form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--space-n); // 32px from Figma
    align-items: stretch;

    @include up-to-tablet {
      grid-template-columns: 1fr;
      gap: var(--space-s);
    }
  }

  &__form-field {
    position: relative;

    // Override Input component width to fit our grid
    :deep(.vc-input) {
      width: 100%;
    }
  }

  &__select {
    width: 100%;
    padding: var(--space-xxs) var(--space-xs); // 8px 16px from Figma
    height: 53.25px; // From input component
    border: 1px solid var(--color-grey);
    border-radius: 10px;
    background: var(--color-white);
    font-family: var(--font-family-poppins);
    font-size: var(--text-b-n);
    color: var(--color-black);
    box-sizing: border-box;
    transition: border-color var(--timing-default) ease;
    appearance: none;
    cursor: pointer;
    padding-right: calc(var(--space-xs) + 24px); // Space for icon

    &:focus {
      outline: none;
      border-color: var(--color-black);
      box-shadow: 0 2px 12px rgb(0, 0, 0, 0.15);
    }

    &:hover {
      border-color: var(--color-black);
      box-shadow: 0 2px 8px rgb(0, 0, 0, 0.1);
    }

    &:invalid {
      color: var(--color-dark-grey);
    }

    @include up-to-tablet {
      height: 44px;
      padding: 6px 12px;
    }
  }

  &__select-icon {
    position: absolute;
    right: var(--space-xs);
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    color: var(--color-black);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
  }

  &__form-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;

    // gap: var(--space-xxs); // 8px from Figma

    @include up-to-tablet {
      flex-direction: column;
      align-items: stretch;

      //   gap: var(--space-s);
    }
  }

  &__form-footer-cta {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 24px;
  }

  &__privacy-link {
    font-family: var(--font-family-poppins);
    font-size: var(--text-b-n);
    color: var(--color-white);
    text-decoration: underline;
    text-underline-offset: 2px;
    transition: color var(--timing-default) ease;

    &:hover {
      color: var(--color-light-grey);
    }

    @include up-to-tablet {
      text-align: center;
    }
  }

  &__right :deep(.form-loader) {
    padding: 0;
    width: 100%;
  }

  &__right :deep(.form-loader__body) {
    color: var(--color-white);
  }

  &__right :deep(.form-loader .form-loader__submitted),
  &__right :deep(.form-loader__body .input__label) {
    color: var(--color-white);
  }

  &__right :deep(.form-loader__body .input__error),
  &__right :deep(.form-loader__body .input-checkbox__error) {
    color: var(--color-white);
  }
}
</style>
